import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AuthenticationService,
  PasswordResetResponse,
} from '../services/authentication.service';
import { clearResetToken, getResetToken } from '../helpers/reset-token.util';
import { LoginNavigationState } from '../login/login.component';

/**
 * The page an emailed reset or invite link opens. `main.ts` has already taken the
 * token out of the fragment (see `reset-token.util.ts`). The page checks the link,
 * then shows either the new-password form or one dead-link state, whatever made
 * the link dead, with a one-click resend that posts the spent token. A check that
 * never reached the server is not a dead link, so it offers a retry instead.
 */
@Component({
  selector: 'app-set-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './set-password.component.html',
  styleUrl: './set-password.component.scss',
})
export class SetPasswordComponent implements OnInit, OnDestroy {
  readonly deadLinkMessage = 'This link has expired or has already been used.';
  // No token at all is most often a refresh: the token lives only in memory once the address bar is cleared, so
  // saying the link expired would be false.
  readonly missingLinkMessage = 'Open the link from your email again to set your password.';
  readonly uncheckedLinkMessage = "We couldn't check your link. Check your connection and try again.";
  passwordRegx: RegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
  readonly maxPasswordLength = 64;

  view: 'checking' | 'form' | 'dead' | 'unchecked' = 'checking';
  token: string | null = null;
  isSubmitting = false;
  isResending = false;
  serverMessage = '';
  resendMessage = '';
  showPassword = false;
  showConfirmPassword = false;

  // A request still in flight when the user leaves must not act on this page's
  // behalf: a late redeem would otherwise sign out a session started since.
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    private toastr: ToastrService
  ) {}

  protected setPasswordForm = new FormGroup(
    {
      password: new FormControl('', [
        Validators.required,
        Validators.maxLength(this.maxPasswordLength),
        Validators.pattern(this.passwordRegx),
      ]),
      confirmPassword: new FormControl('', [Validators.required]),
    },
    { validators: this.passwordsMatch }
  );

  ngOnInit() {
    this.token = getResetToken();
    if (!this.token) {
      // No fragment, or an empty one: nothing to check.
      this.view = 'dead';
      return;
    }
    this.checkLink();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkLink() {
    if (!this.token) {
      return;
    }
    this.view = 'checking';
    this.authService
      .validateResetToken(this.token)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        // The server answers a link it could not check as dead, so any answer is final.
        next: (data) => {
          this.view = this.isSuccess(data) ? 'form' : 'dead';
        },
        // No answer at all says nothing about the link. Keep the token so a retry
        // can check it again; offering resend here would retire a live link.
        error: () => {
          this.view = 'unchecked';
        },
      });
  }

  passwordsMatch(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordMismatchError: true };
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  // Login trims the password it is given, so the stored one must be trimmed too.
  trimPasswords() {
    const { password, confirmPassword } = this.setPasswordForm.value;
    this.setPasswordForm.patchValue({
      password: password?.trim() ?? '',
      confirmPassword: confirmPassword?.trim() ?? '',
    });
  }

  submit() {
    this.trimPasswords();
    this.serverMessage = '';
    if (this.setPasswordForm.invalid || !this.token) {
      this.setPasswordForm.markAllAsTouched();
      return;
    }
    this.isSubmitting = true;
    this.authService
      .redeemResetToken(this.token, this.setPasswordForm.value.password ?? '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.isSubmitting = false;
          if (this.isSuccess(data)) {
            this.finish(data.message, (data.data || undefined)?.email ?? '');
          } else if (data.code === 400 || data.code === 409) {
            // A rejected password or a failed save leaves the link live.
            this.serverMessage = data.message;
          } else {
            this.view = 'dead';
          }
        },
        error: () => {
          this.isSubmitting = false;
          this.toastr.error('An error occurred. Please try again.');
        },
      });
  }

  resend() {
    if (!this.token || this.isResending) {
      return;
    }
    this.isResending = true;
    this.authService
      .resendResetLink(this.token)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.isResending = false;
          this.resendMessage = data.message;
        },
        error: () => {
          this.isResending = false;
          this.toastr.error('An error occurred. Please try again.');
        },
      });
  }

  private isSuccess(data: PasswordResetResponse<unknown>): boolean {
    return data?.code === 200 && data.status === 'SUCCESS';
  }

  // The password changed, so any session this browser holds is now refused.
  // Sign it out here, as logout() does, and let login prefill the address from
  // navigation state rather than a query string.
  private finish(message: string, email: string) {
    localStorage.clear();
    sessionStorage.clear();
    clearResetToken();
    const state: LoginNavigationState = { email, message };
    this.router.navigate(['login'], { state });
  }
}
